import { Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import { ScrollToTop } from "components/ScrollToTop";
import { ProtectedRoute } from "components/ProtectedRoute";
import { useAuth, USER_ROLE } from "contexts/auth/auth.context";
import { Loading } from "./components/Loading";
import { PatientOrGuestRoute } from "./components/PatientOrGuestRoute/PatientOrGuestRoute";
import { getIsZenPilatesApp } from "./helpers/is-zen-pilates-app.helper";
import LoginView from "./views/LoginView/LoginView";
import RedirectPage from "./pages/accessibleFromOutside/RedirectPage";
import AppointmentBookOnlineWidget from "./pages/accessibleFromOutside/AppointmentBookOnlineWidget";
import RegisterPage from "./pages/accessibleFromOutside/Register";

// Lazy-loaded views
const ChangePasswordView = lazy(() => import("views/Profile/ChangePasswordView/ChangePasswordView"));
const VerifyResetPasswordLinkContainer = lazy(() => import("containers/VerifyResetPasswordLinkContainer/VerifyResetPasswordLinkContainer"));
const ChangePasswordModal = lazy(() => import("views/Profile/ChangePasswordModal/ChangePasswordModal"));
const Appointments = lazy(() => import("pages/specialist/Appointments"));
const Tasks = lazy(() => import("pages/specialist/Tasks"));
const Calendar = lazy(() => import("pages/specialist/Calendar"));
const CalendarWithResources = lazy(() => import("pages/specialist/CalendarWithResources"));
const Clients = lazy(() => import("pages/specialist/Clients"));
const Notifications = lazy(() => import("pages/shared/Notifications"));
const Account = lazy(() => import("pages/shared/Account"));
const Templates = lazy(() => import("pages/specialist/Templates"));
const Archive = lazy(() => import("pages/specialist/Archive"));
const AppointmentTypes = lazy(() => import("pages/specialist/AppointmentTypes"));
const Main = lazy(() => import("pages/specialist/Main"));
const Support = lazy(() => import("pages/shared/Support"));
const ClientDetails = lazy(() => import("pages/specialist/ClientDetails"));
const TaskDetails = lazy(() => import("pages/specialist/TaskDetails"));
const AppointmentDetails = lazy(() => import("pages/specialist/AppointmentDetails"));
const Albums = lazy(() => import("pages/specialist/Albums"));
const AlbumDetails = lazy(() => import("pages/specialist/AlbumDetails"));
const AppointmentTypeDetails = lazy(() => import("pages/specialist/AppointmentTypeDetails"));
const NotFound = lazy(() => import("pages/shared/NotFound"));
const TemplateDetails = lazy(() => import("pages/specialist/TemplateDetails"));
const ClientAppointments = lazy(() => import("pages/client/ClientAppointments"));
const ClientNewAppointments = lazy(() => import("pages/client/ZenPilates/ClientNewAppointments"));
const ClientAppointmentDetails = lazy(() => import("pages/client/ClientAppointmentDetails"));
const Tags = lazy(() => import("pages/specialist/Tags"));
const Statistics = lazy(() => import("pages/specialist/Statistics"));
const CommissionSettlement = lazy(() => import("pages/specialist/CommissionSettlement"));
const TagDetails = lazy(() => import("pages/specialist/TagDetails"));
const Events = lazy(() => import("pages/specialist/Events"));
const Reminders = lazy(() => import("pages/specialist/Reminders"));
const ClientReminders = lazy(() => import("pages/client/ClientReminders"));
const Questionnaire = lazy(() => import("pages/specialist/Questionnaire"));
const Documents = lazy(() => import("pages/specialist/Documents"));
const SignDocuments = lazy(() => import("pages/specialist/SignDocuments"));
const ChangesHistory = lazy(() => import("pages/specialist/admin/ChangesHistory"));
const TherapyPlanAppointmentList = lazy(() => import("pages/specialist/TherapyPlanAppointmentList"));
const ClientTherapyPlanAppointmentList = lazy(() => import("pages/specialist/ClientTherapyPlanAppointmentList"));
const AppointmentConfirmation = lazy(() => import("pages/client/AppointmentConfirmation"));
const AppointmentReserveList = lazy(() => import("pages/specialist/AppointmentReserveList"));
const AppointmentBookOnline = lazy(() => import("pages/accessibleFromOutside/AppointmentBookOnline"));
const ClientSignDocuments = lazy(() => import("./pages/client/SignDocuments"));
const BookAppointmentOnlineConfirmation = lazy(() => import("pages/accessibleFromOutside/AppointmentBookOnlineConfirmation"));
const AppointmentRecommendation = lazy(() => import("pages/accessibleFromOutside/AppointmentRecommendation"));
const AvailableDates = lazy(() => import("pages/client/AvailableDates"));
const OnlinePaymentSuccess = lazy(() => import("pages/accessibleFromOutside/OnlinePaymentSuccess"));
const OnlinePaymentFail = lazy(() => import("pages/accessibleFromOutside/OnlinePaymentFail"));
const OnlinePaymentDetails = lazy(() => import("pages/accessibleFromOutside/OnlinePaymentDetails"));
const ClientPurchasedProductList = lazy(() => import("pages/client/ZenPilates/ClientPurchasedProductList"));
const ZenPilatesProductToPurchaseList = lazy(() => import("pages/client/ZenPilates/ZenPilatesProductToPurchaseList"));
const PublicDocumentDownload = lazy(() => import("pages/accessibleFromOutside/PublicDocumentDownload"));
const AppointmentFormPublic = lazy(() => import("pages/accessibleFromOutside/AppointmentFormPublic"));

const AppRoutes = () => {
    const { getIsZenPilates } = useAuth();

    return (
        <>
            <ScrollToTop />
            <Suspense fallback={<Loading isLoading />}>
                <Routes>
                    <Route path="/login" element={<LoginView />} />
                    {getIsZenPilatesApp() &&
                        <Route path="/register" element={<RegisterPage />} />
                    }

                    <Route
                        path="/set-new-password/:token"
                        element={<VerifyResetPasswordLinkContainer />}
                    />
                    <Route
                        path="/change-password-modal"
                        element={<ChangePasswordModal />}
                    />
                    <Route path="/reset-password" element={<ChangePasswordView />} />
                    <Route
                        path="/"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <Main />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/appointments"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <Appointments />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/appointmentReserveList"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <AppointmentReserveList />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/appointmentDetails/:id"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <AppointmentDetails />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/questionnaire/:id"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <Questionnaire />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/calendar"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <Calendar />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/calendar/resources"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <CalendarWithResources />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/clients"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <Clients />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/tasks"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <Tasks />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/notifications"
                        element={
                            <ProtectedRoute
                                allowedRoles={[USER_ROLE.ROLE_DOCTOR, USER_ROLE.ROLE_PATIENT]}
                            >
                                <Notifications />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/appointmentTypes"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <AppointmentTypes />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/appointmentTypeDetails/:id"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <AppointmentTypeDetails />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/archive"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <Archive />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/templates"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <Templates />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/templateDetails/:id"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <TemplateDetails />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/albums"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <Albums />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/albumDetails/:id"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <AlbumDetails />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/tags"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <Tags />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/tagDetails/:id"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <TagDetails />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/support"
                        element={
                            <ProtectedRoute
                                allowedRoles={[USER_ROLE.ROLE_DOCTOR, USER_ROLE.ROLE_PATIENT]}
                            >
                                <Support />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/documents"
                        element={
                            <ProtectedRoute
                                allowedRoles={[USER_ROLE.ROLE_DOCTOR]}
                            >
                                <Documents />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/statistics"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <Statistics />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/commissionSettlement"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <CommissionSettlement />
                            </ProtectedRoute>
                        }
                    />
                    {/*<Route*/}
                    {/*    path="/events"*/}
                    {/*    element={*/}
                    {/*        <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>*/}
                    {/*            <Events />*/}
                    {/*        </ProtectedRoute>*/}
                    {/*    }*/}
                    {/*/>*/}
                    <Route
                        path="/account"
                        element={
                            <ProtectedRoute
                                allowedRoles={[USER_ROLE.ROLE_DOCTOR, USER_ROLE.ROLE_PATIENT]}
                            >
                                <Account />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/clientDetails/:id"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <ClientDetails />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/taskDetails/:id"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <TaskDetails />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/reminders/:appointmentId"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <Reminders />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/signDocuments/:id/:scope"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <SignDocuments />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/changesHistory/:id/:context"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <ChangesHistory />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/therapyPlanAppointmentList/:id"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <TherapyPlanAppointmentList />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/clientTherapyPlanAppointmentList/:id"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>
                                <ClientTherapyPlanAppointmentList />
                            </ProtectedRoute>
                        }
                    />
                    {/*<Route*/}
                    {/*  path="/appointmentEnding/:appointmentId"*/}
                    {/*  element={*/}
                    {/*    <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_DOCTOR]}>*/}
                    {/*      <AppointmentEnding />*/}
                    {/*    </ProtectedRoute>*/}
                    {/*  }*/}
                    {/*/>*/}
                    <Route
                        path="/clientAppointments"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_PATIENT]}>
                                {getIsZenPilates() ? (
                                    <ClientNewAppointments />
                                ): (
                                    <ClientAppointments />
                                )}
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/clientAppointmentDetails/:id"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_PATIENT]}>
                                <ClientAppointmentDetails />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/clientReminders/:appointmentId"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_PATIENT]}>
                                <ClientReminders />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/appointment/confirm/:appointmentId/:patientId"
                        element={
                            <AppointmentConfirmation />
                        }
                    />
                    <Route
                        path="/appointment/recommendation/:appointmentId/:patientId"
                        element={
                            <AppointmentRecommendation />
                        }
                    />
                    <Route
                        path="/clientSignDocuments"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_PATIENT]}>
                                <ClientSignDocuments />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/availableDates"
                        element={
                            <PatientOrGuestRoute>
                                <AvailableDates />
                            </PatientOrGuestRoute>
                        }
                    />
                    <Route
                        path="/book-appointment-online"
                        element={
                            <AppointmentBookOnline />
                        }
                    />
                    <Route
                        path="/umow-wizyte"
                        element={
                            <AppointmentBookOnline />
                        }
                    />
                    <Route
                        path="/book-appointment-online-confirmation"
                        element={
                            <BookAppointmentOnlineConfirmation />
                        }
                    />
                    <Route
                        path="/wizyta/:code"
                        element={
                            <RedirectPage />
                        }
                    />
                    <Route
                        path="/zaproszenie/:code"
                        element={
                            <RedirectPage />
                        }
                    />
                    <Route
                        path="/zalecenia/:code"
                        element={
                            <RedirectPage />
                        }
                    />
                    <Route
                        path="/platnosc/:code"
                        element={
                            <RedirectPage />
                        }
                    />
                    <Route
                        path="/kalendarz/:code"
                        element={
                            <RedirectPage />
                        }
                    />
                    <Route
                        path="/zalecenia/uzytkownik/:code"
                        element={
                            <RedirectPage />
                        }
                    />
                    <Route
                        path="/widget/book-appointment-online"
                        element={
                            <AppointmentBookOnlineWidget />
                        }
                    />
                    <Route
                        path="/payment-success"
                        element={
                            <OnlinePaymentSuccess />
                        }
                    />
                    <Route
                        path="/payment-fail"
                        element={
                            <OnlinePaymentFail />
                        }
                    />
                    <Route
                        path="/appointment/online-payment/details/:id"
                        element={
                            <OnlinePaymentDetails />
                        }
                    />
                    <Route
                        path="/purchasedProductList"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_PATIENT]}>
                                <ClientPurchasedProductList />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/productToPurchase"
                        element={
                            <ProtectedRoute allowedRoles={[USER_ROLE.ROLE_PATIENT]}>
                                <ZenPilatesProductToPurchaseList />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/doc/:fileName"
                        element={
                            <PublicDocumentDownload />
                        }
                    />
                    <Route
                        path="/ankieta/:code"
                        element={
                            <AppointmentFormPublic />
                        }
                    />
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Suspense>
        </>
    );
};

export default AppRoutes;
